/**
 * Vantage Platform Asset Management
 *
 * This module provides client-side utilities for:
 * - Asset availability checking
 * - Symbol normalization (Yahoo Finance format → Vantage format)
 * - Trading deeplink generation
 *
 * This enables the frontend to handle platform-specific logic, eliminating
 * the need for the backend to generate deeplinks.
 */

import { logger } from './logger';

// Vantage platform configuration
const VANTAGE_BASE_URL = "https://secure.vantagemarkets.com/web-trade/trade";

// Temporary mapping from yfinance futures tickers to Vantage spot symbols
const YFINANCE_TO_VANTAGE_MAP: Record<string, string> = {
  "GC=F": "XAUUSD",
  "SI=F": "XAGUSD",
  "PL=F": "XPTUSD",
  "PA=F": "XPDUSD",
};

// Supported assets on Vantage platform (402 instruments total)
// Synced with backend: vantagent/utils/trading.py
const VANTAGE_SUPPORTED_ASSETS: Record<string, string[]> = {
  // 1. Forex (62 pairs)
  forex: [
    "AUDCAD", "AUDCHF", "AUDCNH", "AUDJPY", "AUDNZD", "AUDSGD", "AUDUSD",
    "CADCHF", "CADJPY", "CHFJPY", "CHFSGD",
    "EURAUD", "EURCAD", "EURCHF", "EURCZK", "EURDKK", "EURGBP", "EURHUF",
    "EURJPY", "EURNOK", "EURNZD", "EURPLN", "EURSEK", "EURSGD", "EURTRY", "EURUSD",
    "GBPAUD", "GBPCAD", "GBPCHF", "GBPJPY", "GBPNZD", "GBPSGD", "GBPUSD",
    "NZDCAD", "NZDCHF", "NZDJPY", "NZDSGD", "NZDUSD",
    "SGDJPY",
    "USDBRL", "USDCAD", "USDCHF", "USDCLP", "USDCNH", "USDCOP", "USDCZK",
    "USDDKK", "USDHUF", "USDIDR", "USDILS", "USDINR", "USDJPY", "USDKRW",
    "USDMXN", "USDNOK", "USDPLN", "USDSEK", "USDSGD", "USDTHB", "USDTRY",
    "USDTWD", "USDZAR",
  ],
  // 2. Indices (28 indices)
  indices: [
    "CHINA50", "CHINA50ft", "DJ30", "DJ30ft", "ES35", "EU50",
    "FRA40", "FRA40ft", "GER40", "GER40ft", "HK50", "HK50ft", "HKTECH",
    "JPN225ft", "NAS100", "NAS100ft", "Nikkei225", "SA40", "SGP20",
    "SP500", "SP500ft", "SPI200", "TWINDEX", "UK100", "UK100ft",
    "US2000", "USDX", "VIX",
  ],
  // 3. Precious Metals (8 instruments)
  precious_metals: [
    "XAGAUD", "XAGUSD", "XAUAUD", "XAUEUR", "XAUJPY", "XAUUSD",
    "XPDUSD", "XPTUSD",
  ],
  // 4. Soft Commodities (8 instruments)
  soft_commodities: [
    "Cocoa-C", "Coffee-C", "COPPER-C", "Cotton-C", "OJ-C",
    "Soybean-C", "Sugar-C", "Wheat-C",
  ],
  // 5. Energy (7 instruments)
  energy: [
    "CL-OIL", "GAS-C", "GASOIL-C", "NG-C", "UKOUSD", "UKOUSDft", "USOUSD",
  ],
  // 6. ETFs (41 instruments)
  etfs: [
    "AGG", "ARKG", "ARKK", "ARKQ", "ARKW", "BND", "BNDX", "BNO",
    "DBA", "DBC", "EEM", "EFA", "ESGV", "FAS", "FAZ", "FDN", "FXI",
    "GDX", "GDXJ", "GLD", "GOEX", "GRID", "GSG", "ICLN", "IWM", "IYW",
    "NUGT", "OIH", "QCLN", "QQQ", "SDS", "SPY", "SPYX", "TAN",
    "USL", "USO", "VGT", "VSGX", "XLF", "XLK", "XOP",
  ],
  // 7a. US Shares (78 instruments)
  shares_us: [
    "AALG", "AAPL", "ABBVIE", "ABNB", "ALIBABA", "AMAT", "AMAZON", "AT&T",
    "AVGO", "AXP", "BAC", "BAIDU", "BNTX", "BOEING", "BUD", "CAT",
    "CISCO", "CITI", "CMCSA", "COIN", "CRM", "CVX", "DISNEY", "EXXON",
    "GOOG", "GS", "HD", "HON", "HOOD", "HSBCN", "IBM", "INTEL",
    "JD", "JNJ", "JPM", "KO", "LI", "LMT", "LULU", "MA", "MCD",
    "META", "MMM", "MRNA", "MSFT", "NFLX", "NIO", "NKE", "NTES",
    "NVIDIA", "NVS", "ORCL", "PEP", "PFIZER", "PG", "PM", "PYPL",
    "SBUX", "SE", "SHOP", "SNOW", "SPCE", "SPOT", "TCOM", "TOYOTA",
    "TRMB", "TSLA", "TSM", "U", "UBER", "UL", "UNH", "VISA",
    "VZ", "WFC", "WMT", "XPEV", "ZM",
  ],
  // 7b. UK Shares (26 instruments)
  shares_uk: [
    "AAL", "ABF", "AVIVA", "AZN", "BAE", "BARC", "BATS", "BT.A",
    "CCL", "CPG", "DIAGEO", "GLEN", "GSK", "HSBA", "IMB", "LLOY",
    "NWG", "PRU", "RB", "RELX", "RIO", "SHELL", "STAN", "TESCO",
    "ULVR", "VOD",
  ],
  // 7c. EU Shares (44 instruments)
  shares_eu: [
    "ABN", "ACA", "AGN", "AI", "AIR", "ALV", "ASML", "BAS", "BAYN",
    "BBVA", "BCP", "BNP", "CABK", "CBK", "CON", "CS", "DBK", "EDP",
    "ELE", "EOAN", "FP", "GALP", "GLE", "HEIA", "IBE", "IFX", "INGA",
    "ITX", "LVMH", "MAP", "MT", "OR", "PHIA", "REP", "RNO", "SAB",
    "SANOFI", "SANTANDER", "SAP", "SIE", "SON", "TEF", "UNA", "VIE",
  ],
  // 8. Bonds (7 instruments)
  bonds: [
    "EUB10Y", "EUB2Y", "EUB30Y", "EUB5Y", "EURIBOR3M", "LongGilt", "USNote10Y",
  ],
  // 9. Crypto (66 instruments)
  crypto: [
    "ADAJPY", "ADAUSD", "ALGUSD", "ATMUSD", "AVAUSD", "BATUSD",
    "BCHJPY", "BCHUSD", "BERAUSD", "BNBUSD",
    "BTCBCH", "BTCETH", "BTCEUR", "BTCGLD", "BTCJPY", "BTCLTC", "BTCUSD", "BTCXAU",
    "CRVUSD", "DOGUSD", "DOTUSD", "EOSUSD", "ETCUSD",
    "ETHBCH", "ETHEUR", "ETHGLD", "ETHJPY", "ETHLTC", "ETHUSD", "ETHXAU",
    "FILUSD", "GRTUSD", "HBARUSD", "INCUSD", "IOTUSD", "LNKUSD", "LRCUSD",
    "LTCJPY", "LTCUSD", "MKRUSD", "MTCUSD", "NEOUSD", "NERUSD", "NXPCUSD", "ONDOUSD", "ONEUSD",
    "SANUSD", "SHBUSD", "SOLJPY", "SOLUSD", "SUSUSD", "TRUMPUSD", "TRXUSD",
    "UNIUSD", "USDTJPY", "WIFUSD",
    "XLMJPY", "XLMUSD", "XRPJPY", "XRPUSD", "XTZUSD", "ZECUSD",
  ],
};

/**
 * Normalize a symbol by removing separators and converting to uppercase.
 *
 * This allows matching symbols in different formats:
 * - Yahoo Finance format: BTC-USD, ETH-USD, GC=F
 * - Vantage format: BTCUSD, ETHUSD, XAUUSD
 *
 * @param symbol - Symbol to normalize (e.g., "BTC-USD", "EUR/USD", "AT&T")
 * @returns Normalized symbol (e.g., "BTCUSD", "EURUSD", "ATT")
 *
 * @example
 * normalizeSymbol("BTC-USD") // "BTCUSD"
 * normalizeSymbol("EUR/USD") // "EURUSD"
 * normalizeSymbol("AT&T") // "ATT"
 */
export function normalizeSymbol(symbol: string): string {
  // Remove common separators: dash, slash, space, underscore, period, ampersand, equals
  return symbol.replace(/[-/\s_.&=]/g, '').toUpperCase();
}

export function mapToVantageSymbol(symbol: string): string {
  if (!symbol) {
    return symbol;
  }
  const trimmed = symbol.trim();
  const mapped = YFINANCE_TO_VANTAGE_MAP[trimmed.toUpperCase()];
  return mapped ?? trimmed;
}

/**
 * Get the exact symbol format from Vantage's list.
 *
 * Critical for generating correct deeplinks - Vantage URLs require exact formatting
 * (e.g., "Wheat-C" not "WHEATC", "AT&T" not "ATT").
 *
 * @param symbol - Symbol to look up (can be in any format)
 * @returns Exact symbol format from Vantage's list, or null if not found
 *
 * @example
 * getExactSymbol("btc-usd") // "BTCUSD"
 * getExactSymbol("wheat c") // "Wheat-C"
 * getExactSymbol("att") // "AT&T"
 */
export function getExactSymbol(symbol: string): string | null {
  const normalizedInput = normalizeSymbol(symbol);

  // Search through all asset categories
  for (const symbolsList of Object.values(VANTAGE_SUPPORTED_ASSETS)) {
    for (const exactSymbol of symbolsList) {
      if (normalizeSymbol(exactSymbol) === normalizedInput) {
        return exactSymbol;
      }
    }
  }

  return null;
}

/**
 * Check if an asset is available on Vantage platform.
 *
 * @param symbol - Symbol to check (can be in any format)
 * @returns True if available on Vantage, false otherwise
 *
 * @example
 * isAvailableOnVantage("BTC-USD") // true
 * isAvailableOnVantage("AAPL") // true
 * isAvailableOnVantage("RANDOMSYMBOL") // false
 */
export function isAvailableOnVantage(symbol: string): boolean {
  return getExactSymbol(symbol) !== null;
}

/**
 * Generate a deeplink URL to Vantage trading platform.
 *
 * Critical: Uses exact symbol format from Vantage's list to prevent silent
 * redirects to wrong assets.
 *
 * @param symbol - Trading symbol (in any format: "BTC-USD", "BTCUSD", etc.)
 * @returns Trading deeplink URL if asset available, empty string otherwise
 *
 * @example
 * generateVantageDeeplink("BTC-USD")
 * // "https://secure.vantagemarkets.com/web-trade/trade/BTCUSD?utm_source=vantagent&utm_medium=chat&utm_campaign=chat_to_trading"
 *
 * generateVantageDeeplink("AAPL")
 * // "https://secure.vantagemarkets.com/web-trade/trade/AAPL?utm_source=vantagent&utm_medium=chat&utm_campaign=chat_to_trading"
 *
 * generateVantageDeeplink("RANDOMSYMBOL")
 * // "" (empty string - not available)
 */
export function generateVantageDeeplink(symbol: string): string {
  // Map special tickers (e.g., GC=F -> XAUUSD) before lookup
  const mappedSymbol = mapToVantageSymbol(symbol);

  // Get exact symbol format from Vantage's list
  const exactSymbol = getExactSymbol(mappedSymbol);

  // Return empty string if asset not available on Vantage
  if (!exactSymbol) {
    logger.info(`[Vantage] Asset not available: ${symbol}`);
    return "";
  }

  // Build path-based URL with exact symbol format
  const url = `${VANTAGE_BASE_URL}/${exactSymbol}`;

  // Add UTM parameters for tracking conversions from chat feature
  return `${url}?utm_source=vantagent&utm_medium=chat&utm_campaign=chat_to_trading`;
}

/**
 * Get all supported symbols as a flat list.
 *
 * @returns Array of all supported symbols (402 total)
 */
export function getAllSupportedSymbols(): string[] {
  return Object.values(VANTAGE_SUPPORTED_ASSETS).flat();
}

/**
 * Get supported symbols for a specific asset type.
 *
 * @param assetType - Asset type (e.g., "forex", "shares_us", "precious_metals")
 * @returns Array of supported symbols for that type, or empty array if invalid
 */
export function getSupportedSymbolsByType(assetType: string): string[] {
  return VANTAGE_SUPPORTED_ASSETS[assetType] || [];
}
